import { useCallback, useEffect, useMemo, useState } from 'react';

import { useSimpleModal } from 'lib/modal/simple-modal.hooks';
import {
  DragItem,
  DropItem,
  DropZone,
} from '../../shared/interactive-drag-drop/interactive-drag-drop.constants';
import { ItemToPreview } from '../interactive-response-shared.constants';
import {
  handleItemReturn,
  isDropZoneFull,
} from './interactive-response-shared.utils';
import { ESCAPE } from 'approot/shared/ally/keyboard.hooks';

import popSound from './sound-pop.mp3';
import clickSound from './sound-click.mp3';

export const useInteractiveAudio = () => {
  const [playAudio, setPlayAudio] = useState<boolean>(false);

  const audioFile = useMemo(() => {
    return { pop: new Audio(popSound), click: new Audio(clickSound) };
  }, []);

  const playStart = useCallback(() => {
    if (playAudio) {
      audioFile.pop.play();
    }
  }, [playAudio, audioFile]);

  const playClick = useCallback(() => {
    if (playAudio) {
      audioFile.click.play();
    }
  }, [playAudio, audioFile]);

  const toggleAudio = useCallback(() => {
    setPlayAudio(!playAudio);
  }, [playAudio]);

  return {
    playAudio,
    toggleAudio,
    playStart,
    playClick,
  };
};

export const useInteractiveModal = () => {
  const [startGame, setStartGame] = useState<boolean>(false);
  const { handleClose, handleAfterOpen } = useSimpleModal({
    onClose: () => setStartGame(false),
  });

  return {
    isPlaying: startGame,
    startGame: () => setStartGame(true),
    finishGame: () => setStartGame(false),
    handleClose,
    handleAfterOpen,
  };
};

export const useTileScroll = ({
  containerRef,
  slideCount,
  itemWidth = 80,
}: {
  containerRef: HTMLElement | null;
  slideCount: number;
  itemWidth?: number;
}) => {
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const [prevDisabled, setPrevDisabled] = useState<boolean>(true);
  const [nextDisabled, setNextDisabled] = useState<boolean>(false);

  const moveSlides = useCallback(
    (nextIndex: number) => {
      if (containerRef) {
        const viewportWidth = containerRef.parentElement?.offsetWidth || 0;
        const centeredItemWidth = 200;
        const spacing = 12;
        const centerOffset =
          (viewportWidth - centeredItemWidth) / 2 -
          (itemWidth + spacing) * nextIndex;
        containerRef.style.transform = `translateX(${centerOffset}px)`;
        setPrevDisabled(nextIndex === 0);
        setNextDisabled(nextIndex >= slideCount - 1);
        setCurrentSlide(nextIndex);
      }
    },
    [
      containerRef,
      slideCount,
      itemWidth,
      setPrevDisabled,
      setNextDisabled,
      setCurrentSlide,
    ]
  );

  const handlePrev = useCallback(() => {
    moveSlides(currentSlide - 1);
  }, [currentSlide, moveSlides]);

  const handleNext = useCallback(() => {
    moveSlides(currentSlide + 1);
  }, [currentSlide, moveSlides]);

  useEffect(() => {
    if (containerRef) {
      moveSlides(currentSlide);
    }
    //eslint-disable-next-line
  }, [containerRef]);

  return {
    currentSlide,
    prevDisabled,
    nextDisabled,
    handleNext,
    handlePrev,
    reset: () => moveSlides(0),
  };
};

export const useInteractiveImages = () => {
  const [dragItems, setDragItems] = useState<DragItem[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(-1);

  const handleNextImage = useCallback(() => {
    const index = currentImageIndex + 1;
    setCurrentImageIndex(index);
  }, [currentImageIndex]);

  const handlePrevImage = useCallback(() => {
    const index = currentImageIndex - 1;
    setCurrentImageIndex(index);
  }, [currentImageIndex]);

  const handleImageClick = useCallback(
    (id: string, dragItems: DragItem[]) => {
      setDragItems(dragItems);
      const imageIndex = dragItems.findIndex(
        i => i.question.image?.itemId === id
      );
      setCurrentImageIndex(imageIndex);
    },
    [setDragItems, setCurrentImageIndex]
  );

  const currentItem = useMemo<ItemToPreview | null>(() => {
    const item = dragItems[currentImageIndex];
    return item
      ? {
          itemId: item.id || '',
          imageUrl: item.question?.image?.large || '',
          altText: item.question?.image?.description || '',
          isFit: item.question?.image?.isFit || false,
          text: item.question?.text || null,
          richtext: item.question?.richtext || null,
          baseColour: item.question?.baseColour || null,
          audioUrl: item.question?.audioUrl || null,
          variant: item.question?.variant || null,
        }
      : null;
  }, [currentImageIndex, dragItems]);

  return {
    currentItem,
    isNextDisabled: currentImageIndex === dragItems.length - 1,
    isPrevDisabled: currentImageIndex === 0,
    onNextImage: handleNextImage,
    onPrevImage: handlePrevImage,
    onImageClick: handleImageClick,
    reset: () => setCurrentImageIndex(-1),
  };
};

export const useInteractiveClickOrDrag = () => {
  const [dropZoneSelected, setDropZoneSelected] = useState<string | undefined>(
    undefined
  );
  const [dragItemSelected, setDragItemSelected] = useState<
    DragItem | undefined
  >(undefined);

  const handleItemReturnToStack = useCallback(
    ({
      dragItems,
      dropItems,
      setDragItems,
      setDropItems,
    }: {
      dragItems: DragItem[];
      dropItems: DropItem[];
      setDragItems: (dragItems: DragItem[]) => void;
      setDropItems: (dropItems: DropItem[]) => void;
    }) => {
      if (dragItemSelected) {
        const dragItem = dragItems.find(d => d.id === dragItemSelected.id);
        if (dragItem) {
          const { updatedDragItems, updatedDropItems } = handleItemReturn(
            dragItem,
            dragItems,
            dropItems
          );

          setDragItems(updatedDragItems);
          setDropItems(updatedDropItems);
          setDragItemSelected(undefined);
        }
      }
    },
    [dragItemSelected]
  );

  const handleCancelDropZoneClickMode = useCallback(
    (e: MouseEvent) => {
      const target = e.target as HTMLButtonElement | null;
      const parentWithDataAttr = target?.closest('[data-drop-zone-clickable]');

      if (target && !target.dataset.dropZoneClickable && !parentWithDataAttr) {
        setDropZoneSelected(undefined);
        setDragItemSelected(undefined);
      }
    },
    [setDropZoneSelected, setDragItemSelected]
  );

  // handles the click event for a dropped drag item
  // both in the case where it needs to be selected
  // or a drop zone is selected so the item will be moved to that zone
  const handleDroppedDragItemClick = useCallback(
    ({
      zone,
      droppedDragItem,
      dragItems,
      dropItems,
      setDragItems,
      setDropItems,
      handleOnDrop,
      cancelKeyboardInteraction,
    }: {
      zone: DropZone;
      droppedDragItem: DragItem;
      dragItems: DragItem[];
      dropItems: DropItem[];
      setDragItems: (dragItems: DragItem[]) => void;
      setDropItems: (dropItems: DropItem[]) => void;
      handleOnDrop: (dropZoneId: string, dragItemId: string) => void;
      cancelKeyboardInteraction: () => void;
    }) => {
      if (!!dropZoneSelected) {
        // check there is a free spot
        if (isDropZoneFull(dropItems, dropZoneSelected)) return;

        // move this item
        if (zone.dragItemId === droppedDragItem.id) {
          handleOnDrop(dropZoneSelected || '', droppedDragItem.id);
          setDropZoneSelected(undefined);
          setDragItemSelected(undefined);
        }
      } else {
        // select this item to be moved
        setDragItemSelected(droppedDragItem);
      }
      cancelKeyboardInteraction();
    },
    [dropZoneSelected]
  );

  // handles the click event for a drag item that is NOT dropped yet
  // both in the case where it needs to be selected
  // or a drop zone is selected so the item will be moved to that zone
  const handleDragItemClick = useCallback(
    ({
      dragItem,
      handleOnDrop,
      dropItems,
      cancelKeyboardInteraction,
    }: {
      dragItem: DragItem;
      handleOnDrop: (dropZoneId: string, dragItemId: string) => void;
      dropItems: DropItem[];
      cancelKeyboardInteraction: () => void;
    }) => {
      if (!!dropZoneSelected) {
        // check there is a free spot
        if (isDropZoneFull(dropItems, dropZoneSelected)) return;

        // move this item
        handleOnDrop(dropZoneSelected || '', dragItem.id);
        setDropZoneSelected(undefined);
      } else {
        // select this item to be moved
        setDragItemSelected(dragItem);
        setDropZoneSelected(undefined);
      }
      cancelKeyboardInteraction();
    },
    [dropZoneSelected]
  );

  const handleDropZoneClick = useCallback(
    ({
      handleOnDrop,
      id,
      cancelKeyboardInteraction,
    }: {
      handleOnDrop: (dropZoneId: string, dragItemId: string) => void;
      id: string;
      cancelKeyboardInteraction: () => void;
    }) => {
      if (dragItemSelected) {
        // move the selected drag item to this drop zone
        handleOnDrop(id, dragItemSelected.id);
        setDragItemSelected(undefined);
      } else {
        // select this drop zone
        setDropZoneSelected(id === dropZoneSelected ? undefined : id);
      }
      cancelKeyboardInteraction();
    },
    [dropZoneSelected, dragItemSelected]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const key = e.key;

      if (key === ESCAPE && (dropZoneSelected || dragItemSelected)) {
        e.preventDefault();
        e.stopPropagation();

        setDropZoneSelected(undefined);
        setDragItemSelected(undefined);
        return;
      }
    },
    [dropZoneSelected, dragItemSelected]
  );

  useEffect(() => {
    document.addEventListener('click', handleCancelDropZoneClickMode);
    return () => {
      document.removeEventListener('click', handleCancelDropZoneClickMode);
    };
  }, [handleCancelDropZoneClickMode]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [handleKeyDown]);

  return {
    dropZoneSelected,
    dragItemSelected,
    handleItemReturnToStack,
    handleDroppedDragItemClick,
    handleDragItemClick,
    handleDropZoneClick,
  };
};
